import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  Download,
  FileText,
  RefreshCw,
  Clock,
  User,
  AlertCircle,
  Send,
  CheckCircle,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { StatusBadge } from '../../components/ui/Badge';
import { useAuthStore } from '../../stores/auth-store';
import {
  useProforma,
  useUpdateProforma,
  useGenerateProformaPdf,
  useGenerateProformaXlsx,
  useProformaVersions,
  useTransitionProforma,
} from '../../hooks/useProformas';
import type { ProformaVersionDTO } from '../../hooks/useProformas';
import { MedidasReferencePanel } from '../../components/shared/MedidasReferencePanel';

export function ProformaPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: proforma, isLoading, error } = useProforma(id ?? '');
  const { data: versions, isLoading: versionsLoading } = useProformaVersions(id ?? '');
  const updateProforma = useUpdateProforma();
  const generatePdf = useGenerateProformaPdf();
  const generateXlsx = useGenerateProformaXlsx();
  const transition = useTransitionProforma();
  const { user } = useAuthStore();

  const [latestPdfUrl, setLatestPdfUrl] = useState<string | null>(null);

  // ── Editable fields form (C-13 observaciones, C-14 condiciones) ──
  const form = useForm({
    values: proforma
      ? {
          observaciones: proforma.observaciones ?? '',
          formaPago: proforma.forma_pago ?? '',
          plazo: proforma.plazo ?? '',
          tiempoEntrega: proforma.tiempo_entrega ?? '',
        }
      : undefined,
  });

  const isBorrador = proforma?.estado === 'borrador';
  const isEnviada = proforma?.estado === 'enviada';
  const isEnviadoAlCliente = proforma?.estado === 'enviado_al_cliente';
  const isRechazada = proforma?.estado === 'rechazada';
  const isGerOp = ['gerente', 'operaciones'].includes(user?.role ?? '');
  const isAdminRole = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');
  // Metadata editable in: borrador (all admin), enviada (gerente/operaciones), enviado_al_cliente (all admin)
  const canEditMetadata = isBorrador || (isEnviada && isGerOp) || (isEnviadoAlCliente && isAdminRole);

  const handleSaveFields = async () => {
    if (!id) return;
    const values = form.getValues();
    try {
      await updateProforma.mutateAsync({
        id,
        data: {
          observaciones: values.observaciones || null,
          formaPago: values.formaPago || null,
          plazo: values.plazo || null,
          tiempoEntrega: values.tiempoEntrega || null,
        },
      });
      // Auto-generate new PDF version with the updated conditions/observations
      await handleGeneratePdf();
    } catch {
      // Error handled by api-client toast
    }
  };

  const handleGeneratePdf = async () => {
    if (!id) return;
    try {
      const result = await generatePdf.mutateAsync(id);
      if (result.html) {
        // Local dev: open HTML in new tab
        const blob = new Blob([result.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        setLatestPdfUrl(url);
      } else {
        setLatestPdfUrl(result.download_url);
      }
    } catch {
      // Error handled by api-client toast
    }
  };

  const handleDownload = (url: string, version: number) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `proforma-${proforma?.numero ?? 'draft'}-v${version}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Approval helpers ──
  const canSend = isBorrador && ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');
  const canSendToClient = isEnviada && isAdminRole;
  const canReactivate = isRechazada && isGerOp;

  const handleSend = async () => {
    if (!id) return;
    try {
      await transition.mutateAsync({ proformaId: id, action: 'send' });
    } catch {
      // handled by api-client
    }
  };

  const handleSendToClient = async () => {
    if (!id) return;
    try {
      await transition.mutateAsync({ proformaId: id, action: 'send_to_client' });
    } catch {
      // handled by api-client
    }
  };

  const handleReactivate = async () => {
    if (!id) return;
    try {
      await transition.mutateAsync({ proformaId: id, action: 'reactivate' });
    } catch {
      // handled by api-client
    }
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="lg:col-span-2 h-[600px]" />
          <Skeleton className="h-[600px]" />
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !proforma) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Error al cargar proforma</h2>
          <p className="text-sm text-gray-500 mb-4">
            {error instanceof Error ? error.message : 'Proforma no encontrada'}
          </p>
          <Button variant="secondary" onClick={() => navigate('/proformas')}>
            Volver a proformas
          </Button>
        </Card>
      </div>
    );
  }

  const proformaCode = `PRF-${String(proforma.numero).padStart(4, '0')}`;

  return (
    <div className="p-6 space-y-6 fade-up-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/proformas/${id}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Editor
          </Button>
          <h1 className="text-xl font-bold text-gray-800">
            Vista Previa — {proformaCode}
          </h1>
          <StatusBadge entity="proforma" status={proforma.estado} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left Panel: PDF Preview ── */}
        <div className="lg:col-span-2 space-y-4 order-2 lg:order-none">
          {/* PDF iframe */}
          <Card className="p-0 overflow-hidden">
            {latestPdfUrl || (versions && versions.length > 0) ? (
              <iframe
                src={latestPdfUrl ?? versions?.[0]?.download_url}
                className="w-full h-[700px] border-0"
                title="Vista previa PDF"
              />
            ) : (
              <div className="h-[700px] flex flex-col items-center justify-center text-gray-400">
                <FileText className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium mb-2">No hay PDF generado</p>
                <p className="text-sm mb-6">
                  Genera la primera versión del PDF de esta proforma
                </p>
                <Button onClick={handleGeneratePdf} isLoading={generatePdf.isPending}>
                  <FileText className="w-4 h-4 mr-2" />
                  Generar PDF
                </Button>
              </div>
            )}
          </Card>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleGeneratePdf}
              isLoading={generatePdf.isPending}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generar nueva versión PDF
            </Button>
            {(latestPdfUrl || (versions && versions.length > 0)) && (
              <Button
                variant="secondary"
                onClick={() => {
                  const url = latestPdfUrl ?? versions?.[0]?.download_url;
                  const ver = latestPdfUrl
                    ? (versions?.[0]?.version ?? 0) + 1
                    : versions?.[0]?.version ?? 1;
                  if (url) handleDownload(url, ver);
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const result = await generateXlsx.mutateAsync(id!);
                  // Decode base64 and trigger download
                  const byteCharacters = atob(result.xlsx_base64);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = result.filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                } catch {
                  // handled by api-client toast
                }
              }}
              isLoading={generateXlsx.isPending}
            >
              <FileText className="w-4 h-4 mr-2" />
              Generar Excel
            </Button>
          </div>
        </div>

        {/* ── Right Panel: Metadata & Versions ── */}
        <div className="space-y-4 order-1 lg:order-none">
          {/* Approval Panel — first on mobile so it's immediately visible */}
          {(canSend || canSendToClient || canReactivate) && (
            <Card className="p-4 space-y-3">
              <h3 className="font-semibold text-gray-800">Estado y Aprobación</h3>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Estado actual:</span>
                <StatusBadge entity="proforma" status={proforma.estado} />
              </div>

              <div className="flex flex-col gap-2">
                {/* Send for approval (borrador → enviada) */}
                {canSend && (
                  <Button
                    className="w-full"
                    onClick={handleSend}
                    isLoading={transition.isPending}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar para aprobación
                  </Button>
                )}

                {/* Sent to client (enviada → enviado_al_cliente) */}
                {canSendToClient && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleSendToClient}
                    isLoading={transition.isPending}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Enviado al cliente
                  </Button>
                )}

                {/* Reactivate (rechazada → borrador) */}
                {canReactivate && (
                  <Button
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={handleReactivate}
                    isLoading={transition.isPending}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reactivar proforma
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Editable fields (C-13: Observaciones, C-14: Condiciones) */}
          <Card className="p-4 space-y-4">
            <h3 className="font-semibold text-gray-800">Observaciones y Condiciones</h3>

            {/* C-13: Observaciones */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                Observaciones (C-13)
              </label>
              <textarea
                {...form.register('observaciones')}
                rows={3}
                disabled={!canEditMetadata}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white/80
                  focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none
                  disabled:bg-gray-100 disabled:text-gray-500 resize-none"
                placeholder="Ej: La proforma está sujeta a cantidades proporcionadas por el cliente"
              />
            </div>

            {/* C-14: Condiciones de pago */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                Forma de Pago
              </label>
              <input
                {...form.register('formaPago')}
                disabled={!canEditMetadata}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white/80
                  focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none
                  disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Ej: Transferencia bancaria"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                Plazo
              </label>
              <input
                {...form.register('plazo')}
                disabled={!canEditMetadata}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white/80
                  focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none
                  disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Ej: 30 días"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                Tiempo de Entrega
              </label>
              <input
                {...form.register('tiempoEntrega')}
                disabled={!canEditMetadata}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white/80
                  focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none
                  disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Ej: 15 días hábiles"
              />
            </div>

            {canEditMetadata && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={handleSaveFields}
                isLoading={updateProforma.isPending || generatePdf.isPending}
              >
                Guardar y generar nueva versión PDF
              </Button>
            )}

            {!canEditMetadata && (
              <p className="text-xs text-gray-400 italic">
                No editable en el estado actual
              </p>
            )}
          </Card>

          {/* Version History */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Historial de Versiones
            </h3>

            {versionsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : versions && versions.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {versions.map((version: ProformaVersionDTO) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50
                      hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        Versión {version.version}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <User className="w-3 h-3" />
                        <span>{version.generated_by.nombre}</span>
                        <span>&bull;</span>
                        <span>
                          {new Date(version.generated_at).toLocaleDateString('es-EC', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(version.download_url, version.version)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">
                No hay versiones generadas
              </p>
            )}
          </Card>

          {/* Medidas del residente (reference) */}
          <MedidasReferencePanel projectId={proforma.project_id} />
        </div>
      </div>

    </div>
  );
}
